import { computed, type ComputedRef } from 'vue';
import { useWizardStore } from '@/stores/wizard';
import { sections, type SectionId } from '@/data/learningTest';

export interface LearningScores {
	[key: string]: number;
}

export interface UseLearningScoresReturn {
	modalityScores: ComputedRef<LearningScores>;
	screeningScores: ComputedRef<LearningScores>;
	allScores: ComputedRef<LearningScores>;
	getDominantStyle: () => { type: SectionId; score: number; title: string };
}

/**
 * Composable for calculating learning scores from wizard answers
 * Provides consistent score calculation across all components
 */
export function useLearningScores(): UseLearningScoresReturn {
	const wizardStore = useWizardStore();

	const modalityIds: SectionId[] = ['visual', 'auditory', 'reading', 'kinesthetic'];
	const screeningIds: SectionId[] = [
		'integrated',
		'giftedness',
		'auditoryProcessing',
		'highSensitivity',
		'adhd',
	];

	/**
	 * Calculate percentage score for a section based on answers
	 * Uses section registry for dynamic question counts
	 */
	const calculateSectionScore = (sectionId: SectionId): number => {
		// First, check if we have analysisResult from backend (already percentage)
		if (wizardStore.analysisResult?.sectionScores?.[sectionId] !== undefined) {
			return wizardStore.analysisResult.sectionScores[sectionId];
		}

		// Fallback: calculate percentage from raw answers
		const section = sections.find((s) => s.id === sectionId);
		if (!section?.questionRange) {
			return 0;
		}

		const [start, end] = section.questionRange;
		let rawScore = 0;

		// Sum answers in this section's range
		for (let q = start; q <= end; q++) {
			rawScore += wizardStore.answers[q] || 0;
		}

		// Calculate max raw score: questionCount × 5 (each question scored 1-5)
		const questionCount = end - start + 1;
		const maxRawScore = questionCount * 5;

		// Convert to percentage (0-100)
		return maxRawScore > 0 ? Math.round((rawScore / maxRawScore) * 100) : 0;
	};

	/**
	 * Modality scores (Phase 1: learning preferences)
	 */
	const modalityScores = computed(() => {
		return modalityIds.reduce((acc: LearningScores, id) => {
			acc[id] = calculateSectionScore(id);
			return acc;
		}, {});
	});

	/**
	 * Screening scores (Phase 2: advanced analysis)
	 */
	const screeningScores = computed(() => {
		return screeningIds.reduce((acc: LearningScores, id) => {
			acc[id] = calculateSectionScore(id);
			return acc;
		}, {});
	});

	/**
	 * All scores combined
	 */
	const allScores = computed(() => ({
		...modalityScores.value,
		...screeningScores.value,
	}));

	/**
	 * Get dominant learning style from modality scores
	 */
	const getDominantStyle = () => {
		const scores = modalityScores.value;
		const maxScore = Math.max(...Object.values(scores));
		const dominantType = (Object.keys(scores).find((key) => scores[key] === maxScore) ||
			'visual') as SectionId;

		const titles: Record<string, string> = {
			visual: 'Visueller',
			auditory: 'Auditiver',
			reading: 'Lese-/Schreib',
			kinesthetic: 'Kinästhetischer',
		};

		return {
			type: dominantType,
			score: maxScore,
			title: titles[dominantType] || dominantType,
		};
	};

	return {
		modalityScores,
		screeningScores,
		allScores,
		getDominantStyle,
	};
}
